import { shallowEqual } from "react-redux";
import { useEffect } from "react";
import { type UsedFinanceInsurancePayload, getFinanceInsurancePayloadSelector } from "../utils/financeUtils";
import { setInsuranceQuote } from "../redux/actions/UscInsuranceActions";
import type { UscInsuranceQuoteType } from "../utils/insuranceUtils";
import { hideModal, showModal, updateModalSettings } from "../../../common-deprecated/redux/actions/ModalActions";
import { useUscDispatch, useUscSelector } from "../redux/store";
import { MODAL_USC_INJECTION } from "../../shared-logic/utils/modalConstants";
import { POSTAL_CHANNEL_T1_INSURANCE, POSTAL_TOPIC_USC_T1_RENDERED } from "../../../common-deprecated/constants";
import { getPostal } from "../../../common-deprecated/utils/postalUtils";

const useUscInsuranceCalculatorButton = (): { showInsuranceCalculator: () => void } => {
    const dispatch = useUscDispatch();
    const insuranceGatewayUrl = useUscSelector((state) => state.uscSettings.insuranceGatewayUrl);
    const insuranceCalculatorUrl = `${insuranceGatewayUrl as string}/static/bundle-umd.min.js`;
    // Cast to UsedFinanceInsurancePayload as Stock doenst support insurance for now.
    const configuration = useUscSelector(
        getFinanceInsurancePayloadSelector("insurance"),
        shallowEqual,
    ) as UsedFinanceInsurancePayload;
    const containerId = "mlb-tim-container";

    const onClose = (): void => {
        const mlbTimContainer = document.getElementById(containerId);

        // We also listen to the TIM_WIDGET_CLOSE event as the Makolab calculator can dispatch it to close the modal.
        // (yes these should probably be separate events but that ship has sailed at this point)
        // Attach a 'source' property to avoid double events (see TIM_WIDGET_CLOSE listener below)
        const event = new CustomEvent("TIM_WIDGET_CLOSE", { detail: { source: "USC" } });
        if (mlbTimContainer) {
            dispatch(hideModal(MODAL_USC_INJECTION));
            mlbTimContainer.dispatchEvent(event);
        }
    };

    // Listen for Makolab to send us a rendered event,
    // indicating that "behind the scenes" the finance calculator has been fully loaded.
    // For us that means we should hide the loading spinner, and show the calculator.
    useEffect(() => {
        const channel = POSTAL_CHANNEL_T1_INSURANCE;
        const topic = POSTAL_TOPIC_USC_T1_RENDERED;

        const subscription = getPostal().subscribe({
            channel,
            topic,
            callback: () => {
                dispatch(updateModalSettings({ injectionLoading: false }, MODAL_USC_INJECTION));
            },
        });

        return () => {
            subscription.unsubscribe();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Initiate and show the insurance calculator.
     *
     * Documentation can be found here: https://tim-uat.makodev.pl/story-book/?path=/story/tim-project-introduction--welcome (credentials in 1PW)
     */
    const showInsuranceCalculator = (): void => {
        dispatch(
            showModal({
                type: MODAL_USC_INJECTION,
                containerId,
                title: "",
                onClose,
                onCloseAnimationEnd: onClose,
                injectionLoading: true,
            }),
        );

        let timContainer: HTMLElement | null = null;
        // Wait one frame for the modal to fully initialize, initialize the insurance calculator afterwards.
        window.requestAnimationFrame(() => {
            window.require(
                insuranceCalculatorUrl,
                (insuranceCalculatorFns) => {
                    timContainer = document.getElementById(containerId);
                    if (timContainer) {
                        insuranceCalculatorFns.inject({
                            target: `#${containerId}`,
                            configuration,
                            isAppendedToBody: false,
                        });

                        timContainer.addEventListener(
                            "TIM_WIDGET_CLOSE",
                            // Looks like the event detail object is being edited sometimes and can fall back to 'null'.
                            ((event: CustomEvent<{ source?: "USC" } | null>) => {
                                // Only trigger hideModal if we weren't the source of the event (see above)
                                if (event.detail?.source !== "USC") dispatch(hideModal(MODAL_USC_INJECTION));
                            }) as EventListener,
                            { once: true },
                        );

                        timContainer.addEventListener("TIM_WIDGET_OFFER_CONFIRM", (data) => {
                            const insuranceQuote = (data as Event & { detail: UscInsuranceQuoteType }).detail;
                            dispatch(setInsuranceQuote(insuranceQuote, configuration));
                        });
                    }
                },
                // Error callback: doesn't return anything, though is triggered when something goes wrong.
                (_) => {
                    dispatch(
                        updateModalSettings({ injectionLoading: false, injectionError: true }, MODAL_USC_INJECTION),
                    );
                },
            );
        });
    };

    return { showInsuranceCalculator };
};

export default useUscInsuranceCalculatorButton;
